import React, { useState } from 'react'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { useSortable } from '@/hooks/useSortable'

interface Secret {
  id: number
  name: string
  description: string | null
  agent_count: number
  pending_request_count: number
  created_at: string
}

interface Props {
  secrets: Secret[]
  pending_grant_request_count: number
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'agent_count', label: 'Agents' },
  { value: 'created_at', label: 'Created' },
]

export default function Index({ secrets, pending_grant_request_count }: Props) {
  const { sorted, order, orderBy, handleSort } = useSortable(secrets, 'name')
  const [deleteTarget, setDeleteTarget] = useState<Secret | null>(null)

  function handleDelete() {
    if (deleteTarget) {
      router.delete(`/manage/secrets/${deleteTarget.id}`)
      setDeleteTarget(null)
    }
  }

  return (
    <SidebarLayout
      section="manage"
      title="Secrets"
      action={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Badge badgeContent={pending_grant_request_count} color="warning">
            <Button component={Link} href="/manage/secrets/grant_requests" variant="outlined">
              Grant requests
            </Button>
          </Badge>
          <Button component={Link} href="/manage/secrets/new" variant="contained">
            New secret
          </Button>
        </Box>
      }
    >
      {secrets.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={orderBy}
              label="Sort by"
              onChange={e => handleSort(e.target.value)}
            >
              {SORT_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton size="small" onClick={() => handleSort(orderBy)}>
            {order === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No secrets found.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {sorted.map((secret) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={secret.id}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                  {secret.name}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {secret.description || '—'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                  <Chip label={`${secret.agent_count} agents`} size="small" variant="outlined" />
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                  Created {new Date(secret.created_at).toLocaleDateString()}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Button component={Link} href={`/manage/secrets/${secret.id}/edit`} size="small">
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => setDeleteTarget(secret)}>
                    Delete
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete secret"
        message={`Are you sure you want to delete secret '${deleteTarget?.name}'? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </SidebarLayout>
  )
}
